import { MagnifyingGlassIcon } from '~components/icons'
import React, { InputHTMLAttributes, ElementType, forwardRef } from 'react'
import { COLORS, FONTS, SPACINGS } from './theme'
import { Typography } from '@mui/material'

import { Label } from './label'
// eslint-disable-next-line
import styled from 'styled-components'

import { FieldError, variantToStylesMap, TypographyProps } from './text-area'

const StyledTypography = styled.div<TypographyProps>`
  ${({ variant }) => variantToStylesMap[variant]}
  ${({ color }) => color && `color: ${color};`}
`

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  name: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  value?: string
  icon?: ElementType
  iconAlign?: 'left' | 'right'
  id?: string
  disabled?: boolean
  required?: boolean
  description?: string
  error?: string
  variant?: 'primary' | 'search'
}

interface StyledInputProps {
  hasError?: boolean
  hasIcon?: boolean
  $iconAlign?: 'left' | 'right'
}

export const StyledInput = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 344px;
  min-width: 250px;

  ${StyledTypography} {
    margin-bottom: ${SPACINGS[2]};
  }
`

const StyledSearchInput = styled(StyledInput)`
  width: 240px;
`

const StyledInputFieldWrapper = styled.div`
  position: relative;
  width: 100%;
`

const StyledInputField = styled.input<StyledInputProps>`
  position: relative;
  background-color: ${COLORS.primary.white};
  border-radius: 4px;
  box-sizing: border-box;
  border: 1px solid ${COLORS.secondary.darkBlue.primary};
  border-color: ${(p) => p.hasError && COLORS.feedback.red.error};
  color: ${COLORS.secondary.darkBlue.primary};
  font-size: ${FONTS.sizes.sm};
  width: 100%;
  height: 48px;
  padding: ${SPACINGS[2]};
  padding-left: ${(p) =>
    p.hasIcon && p.$iconAlign === 'left' ? SPACINGS[6] : SPACINGS[2]};
  padding-right: ${(p) =>
    p.hasIcon && p.$iconAlign === 'right' ? SPACINGS[6] : SPACINGS[2]};

  &::placeholder {
    color: ${COLORS.greys.mid};
  }

  &:focus-visible {
    outline: none;
    border: 1px solid ${COLORS.primary.maerskBlue};
  }

  &:hover {
    background-color: ${COLORS.secondary.lightBlue.tint};
    border-color: transparent;
    &::placeholder {
      color: ${COLORS.secondary.darkBlue.primary};
    }
  }

  &:disabled {
    color: ${COLORS.greys.mid};
    border-color: ${COLORS.greys.mid};
    pointer-events: none;
  }
`

const StyledMagnifyingGlassIcon = styled(MagnifyingGlassIcon)`
  position: absolute;
  top: 50%;
  right: ${SPACINGS[2]};
  transform: translateY(-50%);
`

const SearchInputField = styled(StyledInputField)`
  height: 32px;
  padding: 10px 16px;
  text-transform: uppercase;
  border-radius: 20px;
  border: none;
  font-family: ${FONTS.families.headlines.regular};
  font-size: ${FONTS.sizes.xxs};
  color: ${COLORS.secondary.darkBlue};
  text-overflow: ellipsis;
  box-shadow: inset 9px 9px 13px rgb(0 0 0 / 3%);

  &:focus-visible {
    outline: none;
    border: 2px solid ${COLORS.greys.light};
  }

  &:disabled {
    color: ${COLORS.greys.ultraLight};
  }
`

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      disabled,
      error,
      label,
      description,
      name,
      onChange,
      value,
      icon: Icon,
      iconAlign = 'left',
      required,
      className,
      variant = 'primary',
      ...props
    },
    ref,
  ) => {
    const iconStyles = {
      height: '18px',
      width: '18px',
      position: 'absolute',
      top: '50%',
      left: iconAlign === 'left' && SPACINGS[2],
      right: iconAlign === 'right' && SPACINGS[2],
      transform: 'translateY(-50%)',
    }

    return variant === 'primary' ? (
      <StyledInput>
        {label && (
          <Label
            htmlFor={name}
            disabled={disabled}
            label={label}
            required={required}
          />
        )}

        {description && (
          <Typography variant="caption">{description}</Typography>
        )}

        <StyledInputFieldWrapper>
          <StyledInputField
            ref={ref}
            name={name}
            id={name}
            disabled={disabled}
            required={required}
            $iconAlign={iconAlign}
            className={className}
            hasError={!!error}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (onChange) {
                onChange(e)
              }
            }}
            value={value}
            hasIcon={!!Icon}
            {...props}
          />
          {Icon && <Icon style={iconStyles} />}
        </StyledInputFieldWrapper>

        {error && <FieldError>{error}</FieldError>}
      </StyledInput>
    ) : (
      <StyledSearchInput>
        {label && (
          <Label
            htmlFor={name}
            disabled={disabled}
            label={label}
            required={required}
          />
        )}
        {description && (
          <Typography variant="caption">{description}</Typography>
        )}
        <StyledInputFieldWrapper>
          <SearchInputField
            ref={ref}
            name={name}
            id={name}
            disabled={disabled}
            className={className}
            hasError={!!error}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (onChange) {
                onChange(e)
              }
            }}
            value={value}
            {...props}
          />
          <StyledMagnifyingGlassIcon size={20} />
        </StyledInputFieldWrapper>

        {error && <FieldError>{error}</FieldError>}
      </StyledSearchInput>
    )
  },
)

Input.displayName = 'Input'

export default Input
